/* eslint-disable @typescript-eslint/member-ordering */
import { ChangeDetectionStrategy, Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DisplayType } from 'entities/back-counter-tool';
import { Branch } from 'entities/branch';
import { OnChange, OnTouched } from 'entities/control-value-accessor-funcs';
import { Customer } from 'entities/customer';
import { Part } from 'entities/part';
import { PartSearch, Refinement } from 'entities/part-search';
import { PartSearchResponse } from 'entities/parts/part-search-response';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { filter, map, pairwise, scan, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { SourceLocationType } from 'services/logger.service';
import { PartService } from 'services/part.service';
import { ToastService } from 'services/toast.service';
import { TrackerService } from 'services/tracker.service';

@Component({
    selector: 'part-select',
    templateUrl: './part-select.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PartSelectComponent),
            multi: true
        }],
    standalone: false
})
export class PartSelectComponent implements ControlValueAccessor, OnInit  {
  @Input() customer: Customer;
  @Input() branch: Branch;
  @Input() initialSearchTerm: string;
  @Input() displayType: DisplayType;

  private searchSubject: Subject<Partial<PartSearch>> = new ReplaySubject(1);

  private readonly initialSearch = {
    partId: '',
    partSearchTerm: '',
    branchCode: '',
    rewardNumber: '',
    pageNumber: 1,
    pageSize: 20,
    isExpandedSearch: true,
    isCountCheck: false,
    refinements: []
  };

  // loading: true for term changes AND pagination/facet changes (spinner consistency like part-base); we only clear selection on term change
  public loading: boolean = true;
  public partsQuery$: Observable<{ searchCriteria: PartSearch, partsData: PartSearchResponse }> = this.searchSubject
    .pipe(
      // Merge incoming partial updates
      scan((oldSearch, newSearch) => ({
        ...oldSearch,
        partSearchTerm: newSearch.partSearchTerm !== undefined ? newSearch.partSearchTerm : oldSearch.partSearchTerm,
        branchCode: newSearch.branchCode !== undefined ? newSearch.branchCode : oldSearch.branchCode,
        rewardNumber: newSearch.rewardNumber !== undefined ? newSearch.rewardNumber : oldSearch.rewardNumber,
        pageNumber: newSearch.pageNumber !== undefined ? newSearch.pageNumber : oldSearch.pageNumber,
        pageSize: newSearch.pageSize !== undefined ? newSearch.pageSize : oldSearch.pageSize,
        refinements: newSearch.refinements !== undefined ? newSearch.refinements : oldSearch.refinements
      }), this.initialSearch),
      // Require a term before searching
      filter((searchCriteria) => Boolean(searchCriteria.partSearchTerm)),
      // Compare previous and current searches to know what changed
      startWith(null as PartSearch | null),
      pairwise(),
      tap(([prev, curr]) => {
        const termChanged = !prev || prev.partSearchTerm !== curr.partSearchTerm;
        // Always set loading true so part-list spinner can display (matches part-base behavior)
        this.loading = true;
        if (termChanged) {
          // Only clear selection when the primary term changes
          this.part = null;
          this.onChange(null);
        }
      }),
      map(([_, current]) => current),
      switchMap((searchCriteria) =>
        this.partService.searchParts(searchCriteria)
          .pipe(
            map((partsData: any): PartSearchResponse => {
              if (partsData.ErrorType && partsData.ErrorType !== 200) {
                this.toastService.errorMessage('PartBaseComponent', 'getParts', 'getParts', partsData);
                return {
                  parts: [],
                  totalParts: 0,
                  facets: {
                    manufacturer: { field: null, buckets: [] },
                    category: { field: null, buckets: [] }
                  },
                  isExpandedSearch: false
                };
              }
              return partsData;
            }),
            map((partsData) => ({ searchCriteria, partsData }))
          )
      ),
      tap(({ partsData, searchCriteria }) => {
        // End loading after data arrives
        this.loading = false;
        const source = SourceLocationType[SourceLocationType.PartListResult];
        this.trackerService.trackPartSearch(partsData.parts, searchCriteria.partSearchTerm, source);
      }),
      shareReplay(1)
    );

  public part: Part | string;
  private onChange: OnChange<Part | string> = (newValue: Part | string) => { };
  private onTouched: OnTouched = () => { };
  public disabled: boolean = false;

  constructor(
    private partService: PartService,
    private toastService: ToastService,
    private trackerService: TrackerService
  ) { }

  ngOnInit() {
    if(this.displayType === 'back-counter-tool'){
      this.loading = false;
    }
    this.searchSubject.next({
      partSearchTerm: this.initialSearchTerm,
      branchCode: this.branch.code,
      rewardNumber: this.customer.rewardNumber
    });
  }

  updateSearchTerm(searchTerm: string) {
    this.onTouched();
    this.searchSubject.next({ partSearchTerm: searchTerm, pageNumber: 1, refinements: [] });
  }

  updatePage(newPage: number) {
    this.onTouched();
    this.searchSubject.next({ pageNumber: newPage });
  }

  updateRefinements(refinements: Refinement[]) {
    this.onTouched();
    this.searchSubject.next({ refinements, pageNumber: 1 });
  }

  selectPart(part: Part | string) {
    if (
      this.part && part
      && typeof this.part !== 'string' && typeof part !== 'string'
      && this.part.partId === part.partId
    ) {
      this.part = null;
    }
    else {
      this.part = part;
    }
    this.onChange(this.part);
    this.onTouched();
  }

  writeValue(obj: Part): void {
    this.part = obj;
  }

  registerOnChange(fn: OnChange<Part | string>): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: OnTouched): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}